// Modal that lets the user pick which widgets to show on their dashboard.
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, RotateCcw, LayoutGrid } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { WIDGETS, CATEGORIES, DEFAULT_WIDGETS } from "./widgetConfig";

const MAX_ENABLED = 25;

export default function WidgetPicker({ open, enabled, onClose, onSave }) {
  const { t } = useI18n();
  const [selected, setSelected] = useState(enabled);

  useEffect(() => { setSelected(enabled); }, [enabled, open]);

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length < MAX_ENABLED ? [...prev, id] : prev));
  };
  const reset = () => setSelected(DEFAULT_WIDGETS);
  const save = () => { onSave(selected); onClose(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={onClose}
          data-testid="widget-picker"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-3xl p-0 overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b hairline">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-2xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
                  <LayoutGrid size={18} strokeWidth={2} />
                </div>
                <div>
                  <div className="eyebrow">{t("dashboard")}</div>
                  <h3 className="font-display text-[20px] font-bold tracking-tight mt-0.5 leading-tight">{t("widget_picker_title")}</h3>
                  <p className="text-[12px] text-[hsl(var(--text-muted))] mt-1">
                    {t("widget_picker_subtitle")} · <span className="font-semibold tabular-nums text-[hsl(var(--primary))]">{selected.length}/{MAX_ENABLED}</span>
                  </p>
                </div>
              </div>
              <button onClick={onClose} data-testid="widget-picker-close"
                className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] transition-colors shrink-0">
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {CATEGORIES.map((cat) => {
                const widgets = WIDGETS.filter((w) => w.category === cat);
                if (widgets.length === 0) return null;
                return (
                  <div key={cat} className="mb-6 last:mb-0">
                    <div className="eyebrow mb-3">{t(`widget_cat_${cat}`)}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {widgets.map((w) => {
                        const isOn = selected.includes(w.id);
                        const Icon = w.icon;
                        const disabled = !isOn && selected.length >= MAX_ENABLED;
                        return (
                          <button
                            key={w.id}
                            data-testid={`widget-toggle-${w.id}`}
                            onClick={() => toggle(w.id)}
                            disabled={disabled}
                            className={`text-left rounded-2xl border p-3.5 flex items-start gap-3 transition-all
                              ${isOn ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-soft))] bg-[hsl(var(--surface))]"}
                              ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                          >
                            <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${isOn ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"}`}>
                              <Icon size={15} strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[13px] font-semibold text-[hsl(var(--text-strong))] truncate">{t(w.titleKey)}</span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${w.size === 1 ? "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]" : w.size === 2 ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]" : "bg-[hsl(var(--warning-soft,45_92%_92%))] text-[hsl(45_92%_40%)]"}`}>
                                  {w.size === 1 ? "S" : w.size === 2 ? "M" : "L"}
                                </span>
                              </div>
                              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1 leading-snug line-clamp-2">
                                {t(w.descKey)}
                              </div>
                            </div>
                            <div className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5 ${isOn ? "bg-[hsl(var(--primary))] text-white" : "border-2 border-[hsl(var(--border))]"}`}>
                              {isOn && <Check size={11} strokeWidth={3} />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t hairline bg-[hsl(var(--bg))]">
              <button onClick={reset} data-testid="widget-picker-reset" className="btn btn-quiet !text-[hsl(var(--text-muted))]">
                <RotateCcw size={13} strokeWidth={2} /> {t("widget_reset")}
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="btn btn-quiet">{t("cancel")}</button>
                <button onClick={save} data-testid="widget-picker-save" className="btn btn-primary">
                  <Check size={14} strokeWidth={2.2} /> {t("save")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
